import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, Hash, MapPin, GraduationCap } from 'lucide-react';
import { Player } from '@/types/player';
import { PlayerDashboard } from './PlayerDashboard';

interface TeamPlayerCardProps {
  player: Player;
  teamId?: string;
}

export const TeamPlayerCard = ({ player, teamId }: TeamPlayerCardProps) => {
  const playerName = `${player.profile?.first_name || ''} ${player.profile?.last_name || ''}`.trim();

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={player.profile_picture_url} alt={playerName} />
            <AvatarFallback>
              <User className="h-6 w-6" />
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <CardTitle className="text-lg">{playerName}</CardTitle>
            <p className="text-sm text-muted-foreground">{player.profile?.email}</p>
          </div>
          {player.jersey_number && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Hash className="h-3 w-3" />
              {player.jersey_number}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Position:</span>
            <p className="font-medium">{player.position || 'Not specified'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Height:</span>
            <p className="font-medium">{player.height || 'Not specified'}</p>
          </div>
          {player.hometown && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{player.hometown}</span>
            </div>
          )}
          {player.grad_year && (
            <div className="flex items-center gap-1">
              <GraduationCap className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Class of {player.grad_year}</span>
            </div>
          )}
        </div>

        {/* Dashboard KPIs */}
        {teamId && <PlayerDashboard player={player} teamId={teamId} />}
      </CardContent>
    </Card>
  );
};